import { useEffect } from 'react'
import { jsPDF } from 'jspdf'
import { fitPosterImage, haversineKm, niceScaleDistance, rpDateLabel } from '../../utils/poster'

// Map image export (S8). Listens for a `map-export` window event
// { format: 'png' | 'pdf' } (and the legacy `export-map-png`). Snapshots the map
// canvas once it's idle — at the canvas's device-pixel resolution, so retina
// screens already yield a high-resolution image — then downloads a PNG or wraps
// it in a fitted A4-landscape PDF.
export function useMapExport(map, showToast) {
  useEffect(() => {
    const poster = (canvas, dataURL, detail) => {
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' })
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      const margin = 34
      const header = 54
      const footer = 92
      const fitted = fitPosterImage(canvas.width, canvas.height, pageW - margin * 2, pageH - margin * 2 - header - footer)
      const x = (pageW - fitted.width) / 2
      const y = margin + header
      pdf.setFont('helvetica', 'bold'); pdf.setFontSize(22)
      pdf.text(detail.title || 'Projet Résurgence — Carte du monde', margin, margin + 19)
      pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10)
      pdf.text(rpDateLabel(detail.rpDate), margin, margin + 38)
      pdf.addImage(dataURL, 'PNG', x, y, fitted.width, fitted.height)

      const midpoint = canvas.height / 2
      const left = map.current.unproject([0, midpoint])
      const right = map.current.unproject([canvas.width, midpoint])
      const viewportKm = haversineKm(left, right)
      const distance = niceScaleDistance(viewportKm)
      if (distance) {
        const line = fitted.width * distance / viewportKm
        const sy = y + fitted.height + 25
        pdf.setLineWidth(2); pdf.line(x, sy, x + line, sy)
        pdf.line(x, sy - 4, x, sy + 4); pdf.line(x + line, sy - 4, x + line, sy + 4)
        pdf.text(`${distance.toLocaleString('fr-FR')} km`, x, sy + 16)
      }

      const roseX = pageW - margin - 28
      const roseY = y + fitted.height + 30
      pdf.setFont('helvetica', 'bold'); pdf.text('N', roseX - 3, roseY - 22)
      pdf.setLineWidth(1.5); pdf.line(roseX, roseY + 18, roseX, roseY - 14)
      pdf.line(roseX, roseY - 14, roseX - 5, roseY - 5); pdf.line(roseX, roseY - 14, roseX + 5, roseY - 5)

      const legend = document.querySelector('.legend')
      const legendTitle = legend?.querySelector('.legend-title')?.textContent?.trim()
      const labels = Array.from(legend?.querySelectorAll('.legend-label') || []).map(node => node.textContent.trim()).slice(0, 7)
      if (legendTitle) {
        const lx = x + Math.max(line || 0, 150)
        pdf.setFont('helvetica', 'bold'); pdf.text(legendTitle, lx, y + fitted.height + 24)
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8)
        pdf.text(labels.join('  ·  '), lx, y + fitted.height + 39, { maxWidth: pageW - lx - 90 })
      }
      pdf.save(`poster-resurgence-${new Date().toISOString().split('T')[0]}.pdf`)
    }

    const snapshot = (format, detail = {}) => {
      if (!map.current) return
      try {
        map.current.once('idle', () => {
          const canvas = map.current.getCanvas()
          const dataURL = canvas.toDataURL('image/png')
          const stamp = new Date().toISOString().split('T')[0]
          if (format === 'poster') {
            poster(canvas, dataURL, detail)
          } else if (format === 'pdf') {
            const w = canvas.width
            const h = canvas.height
            const pdf = new jsPDF({ orientation: w >= h ? 'landscape' : 'portrait', unit: 'pt', format: 'a4' })
            const pageW = pdf.internal.pageSize.getWidth()
            const pageH = pdf.internal.pageSize.getHeight()
            const scale = Math.min(pageW / w, pageH / h)
            const imgW = w * scale
            const imgH = h * scale
            pdf.addImage(dataURL, 'PNG', (pageW - imgW) / 2, (pageH - imgH) / 2, imgW, imgH)
            pdf.save(`map-${stamp}.pdf`)
          } else {
            const link = document.createElement('a')
            link.href = dataURL
            link.download = `map-plan-${stamp}.png`
            link.click()
          }
        })
      } catch (error) {
        console.error('Error exporting map:', error)
        if (showToast) showToast('Erreur lors de l\'export', 'error')
      }
    }

    const onPng = () => snapshot('png')
    const onExport = (e) => snapshot(e.detail?.format || 'png', e.detail || {})
    window.addEventListener('export-map-png', onPng)
    window.addEventListener('map-export', onExport)
    return () => {
      window.removeEventListener('export-map-png', onPng)
      window.removeEventListener('map-export', onExport)
    }
  }, [map, showToast])
}
